import { type Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { getProductParticipationConfirmationLink } from '@adornis/buchungsheld-db/api/participation.js';
import { Product } from '@adornis/buchungsheld-db/entities/products.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as QRCodeLibrary from 'qrcode';
import { filter, switchMap } from 'rxjs';

@customElement('bh-prompt-product-participation-qr')
export class BHPromptProductParticipationQR extends TrDialog<void> {
  static override get element_name() {
    return 'bh-prompt-product-participation-qr';
  }

  @property({ attribute: false }) productID = new RXController<Maybe<string>>(this, undefined);

  @state() private readonly product = new RXController(
    this,
    this.productID.observable.pipe(
      filter(Boolean),
      switchMap(productID => {
        return subscribeByID(Product, productID)({ name: 1 });
      }),
    ),
  );

  @state() private readonly confirmationLink = new RXController(
    this,
    this.productID.observable.pipe(
      filter(Boolean),
      switchMap(productID => {
        return getProductParticipationConfirmationLink(productID)({ url: 1 });
      }),
      switchMap(async link => {
        const qrCode = await QRCodeLibrary.toDataURL(link.url);
        return {
          url: link.url,
          qrCode,
        };
      }),
    ),
  );

  override render() {
    if (this.confirmationLink.isLoading || !this.confirmationLink.value)
      return html` <x-loading-state></x-loading-state> `;

    const link = this.confirmationLink.value;
    const item = html`
      <x-flex space="xs" crossaxis-center>
        <!-- hadding -->
        <x-flex horizontal space="md" crossaxis-center center space-between>
          <x-h3> ${this.product.value?.name} </x-h3>
          <tr-button-small
            .color=${this.colors.secondary.shade(300)}
            icon="content_copy"
            .action=${() => this.copyLink(link.url)}
          >
            <x-text>${this._translation.translate('copy')}</x-text>
          </tr-button-small>
        </x-flex>

        <!-- qrcode -->
        <img src="${link.qrCode}" alt="QR Code" />
      </x-flex>
    `;

    return html` <x-flex space="md"> ${item} </x-flex> `;
  }

  copyLink(link: string) {
    navigator.clipboard.writeText(link).then(() => XSnackbar.show(this._translation.translate('copied')));
  }

  override async cancel(returnVal?: any): Promise<void> {
    await this.close();
  }
}
